/*
 *  Availability Filter
 *    Custom filter for filtering products by availability (in stock / out of stock)
 *
 *  Version:
 *    1.0.0 - 2024/01/01
 */

import { Filter } from '@process-creative/slate-ajax-collection';

export class AvailabilityFilter extends Filter {
  constructor(template) {
    super(template, 'availability');
    this.availability = []; // Array of boolean values: [] = show all, [true] = in stock only, [false] = out of stock only, [true, false] = show all
  }

  // Set the availability filter (use setSettings instead)
  setAvailability(availability) {
    if (Array.isArray(availability)) {
      this.availability = availability;
    } else if (availability === null || availability === undefined) {
      this.availability = [];
    } else {
      this.availability = [availability];
    }
    // Trigger the filter update manually
    this.template.filters.onFilterUpdate();
  }

  // Clear the availability filter (use setSettings instead)
  clear() {
    this.availability = [];
    // Trigger the filter update manually
    this.template.filters.onFilterUpdate();
  }

  // Set filter settings (standard method)
  setSettings(settings) {
    if (settings === null || settings === undefined) {
      this.availability = [];
    } else if (Array.isArray(settings)) {
      this.availability = settings;
    } else if (typeof settings === 'boolean') {
      this.availability = [settings];
    } else if (typeof settings === 'string') {
      try {
        const parsed = JSON.parse(settings);
        if (Array.isArray(parsed)) {
          this.availability = parsed;
        } else {
          const boolValue = settings === 'true' ? true : settings === 'false' ? false : null;
          this.availability = boolValue !== null ? [boolValue] : [];
        }
      } catch {
        const boolValue = settings === 'true' ? true : settings === 'false' ? false : null;
        this.availability = boolValue !== null ? [boolValue] : [];
      }
    } else {
      this.availability = [];
    }
    // Note: Don't call onFilterUpdate here as it's called by the system when loading from URL
  }

  // Check if filter is active
  isActive() {
    return this.availability.length > 0;
  }

  // Get current filter value
  getValue() {
    return this.availability;
  }

  // Filter products based on availability
  // This method is called for each product individually by the collection system
  filter(product, variant) {
    if (this.availability.length === 0) {
      return true; // Show all products
    }
    
    // If both true and false are selected, show all products
    if (this.availability.includes(true) && this.availability.includes(false)) {
      return true;
    }
    
    // Otherwise, check if product availability matches any selected value
    return this.availability.includes(product.available);
  }

  // Get filter settings for URL
  getSettings() {
    if (this.availability.length === 0) return null;
    return JSON.stringify(this.availability);
  }
}
